import React, { useEffect, useState } from "react";
import { useSignPersonalMessage } from "@mysten/dapp-kit";
import { Gift, X } from "lucide-react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { getWalletProof } from "@/lib/walletProof";
import { api, shortAddr } from "@/lib/api";

// Shows a dismissible banner when the user arrived via a ?ref= link. Binding the referral
// now requires the trader to SIGN a message (wallet-ownership + consent), so nobody can
// attach a stranger's wallet to their referral code. First-touch only.
export default function ReferralBindBanner() {
  const { address, connected } = useWallet();
  const { mutateAsync: signPersonalMessage } = useSignPersonalMessage();
  const [ref, setRef] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const r = localStorage.getItem("suipump_ref");
    setRef(r || null);
  }, [connected]);

  if (!connected || !ref) return null;

  const activate = async () => {
    setBusy(true);
    try {
      const proof = await getWalletProof(signPersonalMessage, address, "bind_referral", ref);
      const { data } = await api.post("/referral/bind", { trader: address, ref, ...proof });
      localStorage.removeItem("suipump_ref");
      setRef(null);
      toast.success(data.bound ? "Referral activated 🎉" : "Referral link was not valid");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate referral");
    } finally { setBusy(false); }
  };

  const dismiss = () => { localStorage.removeItem("suipump_ref"); setRef(null); };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-4" data-testid="referral-bind-banner">
      <div className="rounded-xl border border-[#1EC62E]/30 px-4 py-3 flex items-center gap-3 flex-wrap" style={{ background: "linear-gradient(90deg,rgba(124,242,78,0.08),transparent)" }}>
        <Gift className="w-5 h-5 text-[#1EC62E] shrink-0" />
        <div className="flex-1 min-w-[180px] text-sm">
          You were referred by <span className="font-mono text-[#1EC62E]">{ref.startsWith("0x") ? shortAddr(ref) : `@${ref}`}</span>. Activate to support them — they earn a share of the trade fee, at no extra cost to you.
        </div>
        <button onClick={activate} disabled={busy} data-testid="referral-activate-button" className="shrink-0 px-4 py-2 rounded-lg font-bold text-xs text-black disabled:opacity-50" style={{ background: "linear-gradient(90deg,#7CF24E,#1EC62E)" }}>
          {busy ? "Confirm in wallet…" : "Activate (sign)"}
        </button>
        <button onClick={dismiss} data-testid="referral-dismiss-button" aria-label="Dismiss" className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-white"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
